export const USER_TYPES = {
  SME: 'sme',
  FINANCIAL_INSTITUTION: 'financial_institution',
} as const;

export type UserType = typeof USER_TYPES[keyof typeof USER_TYPES];

type AppId = 'admin' | 'accounting' | 'portfolio' | 'business';

interface AppConfig {
  id: AppId;
  name: string;
  description: string;
  icon: string;
  domain: string;
  requiredRole: string;
  isExternal?: boolean;
  mobileOnly?: boolean;
}

// Configuration des URLs des applications externes
export const APP_URLS = {
  // Application de comptabilité
  ACCOUNTING: import.meta.env.VITE_ACCOUNTING_URL,
  
  // Application de gestion de portefeuille
  PORTFOLIO: import.meta.env.VITE_PORTFOLIO_URL,
  
  // Application de gestion commerciale (mobile)
  BUSINESS_ANDROID: import.meta.env.VITE_BUSINESS_ANDROID_URL,
  BUSINESS_IOS: import.meta.env.VITE_BUSINESS_IOS_URL,
};

// Configuration des sous-domaines pour chaque application
export const APP_DOMAINS = {
  [USER_TYPES.SME]: {
    admin: 'http://localhost:4173/',
    accounting: APP_URLS.ACCOUNTING,
    portfolio: APP_URLS.PORTFOLIO,
    business: APP_URLS.BUSINESS_ANDROID, // Par défaut Android, sera géré dynamiquement
  },
  [USER_TYPES.FINANCIAL_INSTITUTION]: {
    portfolio: APP_URLS.PORTFOLIO,
  },
} as const;

export const APPS_CONFIG: Record<UserType, Record<AppId, AppConfig>> = {
  [USER_TYPES.SME]: {
    admin: {
      id: 'admin',
      name: 'Administration',
      description: 'Gérez votre profil entreprise et vos utilisateurs',
      icon: 'Settings',
      domain: APP_DOMAINS[USER_TYPES.SME].admin,
      requiredRole: 'admin',
    },
    accounting: {
      id: 'accounting',
      name: 'Comptabilité',
      description: 'Gérez votre comptabilité et vos finances',
      icon: 'Calculator',
      domain: APP_DOMAINS[USER_TYPES.SME].accounting,
      requiredRole: 'user',
      isExternal: true,
    },
    portfolio: {
      id: 'portfolio',
      name: 'Gestion de Portefeuille PME',
      description: 'Sollicitez des crédits, gérez les remboursements, les leasings, et profitez du capital investissement',
      icon: 'LineChart',
      domain: APP_DOMAINS[USER_TYPES.SME].portfolio,
      requiredRole: 'user',
      isExternal: true,
    },
    business: {
      id: 'business',
      name: 'Gestion Commerciale',
      description: 'Gérez vos ventes, clients et inventaire sur mobile',
      icon: 'Briefcase',
      domain: APP_DOMAINS[USER_TYPES.SME].business,
      requiredRole: 'user',
      isExternal: true,
      mobileOnly: true,
    },
  },
  [USER_TYPES.FINANCIAL_INSTITUTION]: {
    portfolio: {
      id: 'portfolio',
      name: 'Gestion de Portefeuille',
      description: 'Gérez les portefeuilles de vos clients PME',
      icon: 'BarChart',
      domain: APP_DOMAINS[USER_TYPES.FINANCIAL_INSTITUTION].portfolio,
      requiredRole: 'user',
      isExternal: true,
    },
  } as Record<AppId, AppConfig>,
};

// Fonction utilitaire pour détecter le type d'appareil
export const getDeviceType = () => {
  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
  
  if (/android/i.test(userAgent)) {
    return 'android';
  }
  
  if (/iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream) {
    return 'ios';
  }
  
  return 'desktop';
};

// Fonction pour vérifier si une application est disponible
export const isAppAvailable = (appType: AppId): boolean => {
  switch (appType) {
    case 'accounting':
      return !!APP_URLS.ACCOUNTING;
    case 'portfolio':
      return !!APP_URLS.PORTFOLIO;
    case 'business':
      return !!APP_URLS.BUSINESS_ANDROID || !!APP_URLS.BUSINESS_IOS;
    default:
      return false;
  }
};

// Fonction pour obtenir le statut d'une application
export const getAppStatus = (appType: AppId): 'available' | 'unavailable' | 'mobile-only' => {
  if (!isAppAvailable(appType)) {
    return 'unavailable';
  }
  
  if (appType === 'business') {
    return 'mobile-only';
  }
  
  return 'available';
};

// Fonction pour ouvrir l'application appropriée selon le contexte
export const openExternalApp = (appType: AppId) => {
  try {
    switch (appType) {
      case 'accounting':
        if (!APP_URLS.ACCOUNTING) {
          throw new Error('URL de l\'application Comptabilité non configurée');
        }
        window.open(APP_URLS.ACCOUNTING, '_blank');
        break;
        
      case 'portfolio':
        if (!APP_URLS.PORTFOLIO) {
          throw new Error('URL de l\'application Gestion de Portefeuille non configurée');
        }
        window.open(APP_URLS.PORTFOLIO, '_blank');
        break;
        
      case 'business':
        const deviceType = getDeviceType();
        if (deviceType === 'android') {
          if (!APP_URLS.BUSINESS_ANDROID) {
            throw new Error('URL du Play Store non configurée');
          }
          window.open(APP_URLS.BUSINESS_ANDROID, '_blank');
        } else if (deviceType === 'ios') {
          if (!APP_URLS.BUSINESS_IOS) {
            throw new Error('URL de l\'App Store non configurée');
          }
          window.open(APP_URLS.BUSINESS_IOS, '_blank');
        } else {
          // Pour desktop, afficher un message informatif
          alert('L\'application de Gestion Commerciale est disponible uniquement sur mobile. Veuillez télécharger l\'application depuis votre store mobile (Google Play Store ou App Store).');
        }
        break;
        
      default:
        throw new Error(`Type d'application non reconnu: ${appType}`);
    }
  } catch (error) {
    console.error('Erreur lors de l\'ouverture de l\'application:', error);
    alert(`Impossible d'ouvrir l'application: ${error instanceof Error ? error.message : 'Erreur inconnue'}`);
  }
};